// マルチプロバイダLLMクライアントの共通層。
// 会話履歴はGemini互換の Content/Part 形式を中立フォーマットとして保持し
// （localStorageの既存データと互換）、各プロバイダのアダプタが送受信時に変換する。

import { generateGemini } from './gemini.js'
import { generateAnthropic } from './anthropic.js'
import { generateOpenAI } from './openai.js'

export type Provider = 'gemini' | 'anthropic' | 'openai'

export const PROVIDER_LABELS: Record<Provider, string> = {
  gemini: 'Gemini',
  anthropic: 'Claude (Anthropic)',
  openai: 'OpenAI',
}

export interface FunctionCall {
  name: string
  args: Record<string, unknown>
  /** Claude/OpenAIのツール呼び出しID。tool_result との対応付けに必要（Geminiでは未使用） */
  id?: string
}

export interface ChatPart {
  text?: string
  thought?: boolean
  thoughtSignature?: string
  /** 思考ブロックの生成元。プロバイダ固有の署名を他プロバイダへ送らないための目印。
      未設定はGemini産（マルチプロバイダ対応以前の保存データ互換） */
  thoughtProvider?: Provider
  /** Claude の redacted_thinking ブロックのペイロード */
  redactedThinking?: string
  functionCall?: FunctionCall
  functionResponse?: {
    name: string
    response: Record<string, unknown>
    /** 対応する functionCall の id（Claude/OpenAI用） */
    id?: string
  }
}

export interface ChatContent {
  role: 'user' | 'model'
  parts: ChatPart[]
}

export interface FunctionDeclaration {
  name: string
  description: string
  parameters: Record<string, unknown>
}

export interface ThinkingConfig {
  thinkingLevel?: string
  thinkingBudget?: number
}

export interface ModelConfig {
  provider: Provider
  modelId: string
  thinkingConfig?: ThinkingConfig
}

export interface GenerateRequest {
  apiKey: string
  model: ModelConfig
  contents: ChatContent[]
  systemInstruction: string
  functionDeclarations: FunctionDeclaration[]
}

export interface Candidate {
  content?: ChatContent
  finishReason?: string
}

export interface GenerateResponse {
  candidates?: Candidate[]
  promptFeedback?: { blockReason?: string }
}

export class LlmApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly provider: Provider = 'gemini',
  ) {
    super(message)
    this.name = 'LlmApiError'
  }
}

// 思考レベル指定として受け付ける値（プロバイダごとに解釈は異なる）
// - Gemini: thinkingLevel（high〜minimal）または thinkingBudget（数値）
// - Claude: output_config.effort（minimal は low に丸める。none/0 は思考オフ）
// - OpenAI: reasoning_effort（xhigh/max は high に丸める）
const THINKING_LEVELS = ['high', 'medium', 'low', 'minimal', 'xhigh', 'max', 'none']

const MODEL_ID_PATTERN = /^[a-zA-Z0-9._-]+$/

/** モデルIDのプレフィックスからプロバイダを判定する。不明なIDは従来互換でGemini扱い */
export function detectProvider(modelId: string): Provider {
  const id = modelId.toLowerCase()
  if (id.startsWith('claude')) return 'anthropic'
  if (id.startsWith('gpt') || id.startsWith('chatgpt') || /^o\d/.test(id)) return 'openai'
  return 'gemini'
}

/**
 * 設定画面のモデル選択値（例: "gemini-3.5-flash:high", "claude-sonnet-5:high",
 * "gpt-5.1:medium", カスタム "my-tuned-model"）を ModelConfig に変換する。
 * ":" の後ろは思考レベル（high/medium/low/minimal/xhigh/max/none）または
 * thinkingBudget（整数、Gemini用）。
 */
export function parseModelSelection(value: string): ModelConfig {
  const [rawModelId = '', thinkingValue] = value.split(':', 2)
  const modelId = rawModelId.trim()

  if (!modelId || !MODEL_ID_PATTERN.test(modelId)) {
    throw new LlmApiError(400, `モデルIDの形式が不正です: "${value}"`)
  }

  const provider = detectProvider(modelId)

  if (thinkingValue === undefined || thinkingValue === '') {
    return { provider, modelId }
  }

  if (THINKING_LEVELS.includes(thinkingValue)) {
    return { provider, modelId, thinkingConfig: { thinkingLevel: thinkingValue } }
  }

  const budget = Number.parseInt(thinkingValue, 10)
  if (!Number.isNaN(budget)) {
    return { provider, modelId, thinkingConfig: { thinkingBudget: budget } }
  }

  return { provider, modelId }
}

// ---- 共通リトライ付きPOST ----

const MAX_RETRIES = 2
const BACKOFF_MS = [3000, 6000]
// レートリミット(429)時にAPIが提案する待機時間の上限。
// 無料枠の日次クォータ切れ等で数十秒〜の待機を提案されても、UIを長時間固めない
const MAX_RETRY_DELAY_MS = 15000

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export interface PostJsonSpec {
  provider: Provider
  url: string
  headers: Record<string, string>
  body: unknown
  /** リトライ対象のHTTPステータス */
  retryableStatuses: Set<number>
  /** エラーレスポンスからメッセージを取り出す */
  extractErrorMessage: (json: unknown) => string | undefined
  /** エラーレスポンス/ヘッダーからAPI提案の待機時間(ms)を取り出す */
  extractRetryDelayMs?: (json: unknown, response: Response) => number | undefined
  connectErrorMessage: string
}

/** JSONをPOSTし、過負荷系ステータスはバックオフ付きでリトライする */
export async function postJsonWithRetry(spec: PostJsonSpec): Promise<unknown> {
  const body = JSON.stringify(spec.body)
  let lastError: LlmApiError | undefined

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    let response: Response
    try {
      response = await fetch(spec.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...spec.headers },
        body,
      })
    } catch {
      throw new LlmApiError(0, spec.connectErrorMessage, spec.provider)
    }

    const text = await response.text()
    let json: unknown
    try {
      json = JSON.parse(text)
    } catch {
      json = undefined
    }

    if (response.ok) {
      return json
    }

    lastError = new LlmApiError(
      response.status,
      spec.extractErrorMessage(json) ?? `HTTP ${response.status}`,
      spec.provider,
    )

    // 過負荷・レートリミットはバックオフ付きでリトライ。
    // APIが待機時間（RetryInfo / Retry-After）を返した場合は、上限内でそれに従う
    if (spec.retryableStatuses.has(response.status) && attempt < MAX_RETRIES) {
      const backoff = BACKOFF_MS[attempt] ?? 6000
      const suggested = spec.extractRetryDelayMs?.(json, response) ?? 0
      await sleep(Math.min(Math.max(backoff, suggested), MAX_RETRY_DELAY_MS))
      continue
    }
    break
  }

  throw lastError ?? new LlmApiError(0, '不明なエラー', spec.provider)
}

/** Retry-After ヘッダー（秒数形式）から待機時間を取り出す */
export function retryAfterMs(response: Response): number | undefined {
  const value = response.headers.get('retry-after')
  if (!value) return undefined
  const seconds = Number.parseFloat(value)
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000
  return undefined
}

/** モデル設定のプロバイダに応じたAPIへリクエストを振り分ける */
export async function generateContent(request: GenerateRequest): Promise<GenerateResponse> {
  switch (request.model.provider) {
    case 'anthropic':
      return generateAnthropic(request)
    case 'openai':
      return generateOpenAI(request)
    default:
      return generateGemini(request)
  }
}
